/**
 * utils/citationParser.js
 *
 * Parses markdown links from public chat model responses and converts
 * them into citation objects for rendering as pills.
 *
 * ChatGPT search models return responses with inline markdown links like:
 *   "S&P 500 futures are down 0.7% (apnews.com)"
 * which are actually markdown: [apnews.com](https://apnews.com/article/...)
 *
 * This utility:
 *   1. Extracts all [text](url) links from the response
 *   2. Builds a deduplicated citations array
 *   3. Replaces inline links with numbered markers [1], [2], etc.
 *   4. Returns { cleanedText, citations }
 */

/**
 * Extract domain from a URL.
 * @param {string} url
 * @returns {string}
 */
function extractDomain(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

/**
 * Parse markdown links from text and build citations.
 *
 * @param {string} text — Raw response text from the model
 * @returns {{ cleanedText: string, citations: Array<{ index: number, url: string, domain: string, label: string }> }}
 */
export function parseLinksIntoCitations(text) {
  if (!text) return { cleanedText: text, citations: [] };

  // Match markdown links: [text](url)
  const linkRegex = /\[([^\]]*)\]\((https?:\/\/[^)]+)\)/g;

  // First pass: collect unique URLs and assign indices
  const urlMap = new Map(); // url -> { index, domain, label }
  let match;

  // Reset regex
  linkRegex.lastIndex = 0;
  while ((match = linkRegex.exec(text)) !== null) {
    const [, linkText, url] = match;
    if (!urlMap.has(url)) {
      const index = urlMap.size + 1;
      const domain = extractDomain(url);
      // Use the link text as label if it's meaningful, otherwise use domain
      const label = linkText && linkText.length > 2 ? linkText : domain;
      urlMap.set(url, { index, url, domain, label, doc_id: url });
    }
  }

  if (urlMap.size === 0) return { cleanedText: text, citations: [] };

  // Second pass: replace links with [N] markers
  linkRegex.lastIndex = 0;
  let cleanedText = text.replace(linkRegex, (fullMatch, linkText, url) => {
    const citation = urlMap.get(url);
    if (citation) {
      return `[${citation.index}]`;
    }
    return fullMatch;
  });

  // Third pass: clean up leftover parentheses wrapping citation markers
  // Handles: "( [1] )" → "[1]", "([1])" → "[1]", "( [1])" → "[1]"
  cleanedText = cleanedText.replace(/\(\s*(\[\d+\])\s*\)/g, "$1");

  // Also clean up multiple spaces left behind
  cleanedText = cleanedText.replace(/  +/g, " ");

  // Build citations array sorted by index
  const citations = Array.from(urlMap.values()).sort(
    (a, b) => a.index - b.index,
  );

  return { cleanedText, citations };
}

/**
 * Check if a text contains markdown links that could be citations.
 * Quick check before doing full parsing.
 *
 * @param {string} text
 * @returns {boolean}
 */
export function hasMarkdownLinks(text) {
  if (!text) return false;
  return /\[[^\]]*\]\(https?:\/\/[^)]+\)/.test(text);
}
